/* eslint-disable react/react-in-jsx-scope */
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { LoadingSpinner } from "@/components/ui/loadingSpinner";
import { toast } from "@/components/ui/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

const LoginForm = () => {
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect");
  const navigate = useNavigate();
  const { signIn, user, logout } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [formIsLoading, setFormIsLoading] = useState(false);
  const { t } = useTranslation("auth", {
    keyPrefix: "login",
  });

  const formSchema = z.object({
    email: z.string().email({ message: t("Adresse email invalide") }),
    password: z.string().min(2, {
      message: t("Le mot de passe doit contenir au moins 2 caractères."),
    }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  useEffect(() => {
    if (user !== null && user?.roles?.includes("ROLE_FUNDER")) {
      toast({
        title: t("Vous êtes connecté"),
      });
      window.location.href = `/`;
    } else if (user !== null && !user?.roles?.includes("ROLE_FUNDER")) {
      logout();
      setError("password", {
        type: "server",
        message: t("Email ou mot de passe incorrect"),
      });
    }
  }, [user]);

  const { setError } = form;

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setFormIsLoading(true);

    const { success } = await signIn(values.email, values.password);

    if (!success) {
      // Déclenche une erreur pour le champ mot de passe
      setError("password", {
        type: "server",
        message: t("Email ou mot de passe incorrect"),
      });
    }
    setFormIsLoading(false);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Déjà un compte ?")}</CardTitle>
        <CardDescription>
          {t("Accédez à votre espace et gérez vos projets.")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("Mail")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("Mail")} {...field} type="email" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("Mot de passe")}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder={t("Mot de passe")}
                        {...field}
                        type={showPassword ? "text" : "password"}
                      />
                      <div
                        onClick={(e) => {
                          e.preventDefault();
                          setShowPassword(!showPassword);
                        }}
                        className="absolute right-2 top-1/2 flex size-5 -translate-y-1/2 cursor-pointer items-center justify-center"
                      >
                        {!showPassword ? <EyeOff /> : <Eye />}
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end">
              <Link to="/forgot-password" className="underline">
                {t("Mot de passe oublié ?")}
              </Link>
            </div>
            <Button
              type="submit"
              variant={"default"}
              className="w-full bg-[#1d5214] text-white hover:bg-[#276e1b]"
            >
              {formIsLoading ? <LoadingSpinner /> : t("Se connecter")}
            </Button>
          </form>
          <div className="mt-4 text-center">
            <a
              className="inline-flex items-center gap-1 text-sm text-primary underline hover:text-primary/80 transition-colors"
              href="https://verdeo.fr/contact/"
              target="_blank"
              rel="noreferrer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-user-plus h-4 w-4"
              >
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <line x1="19" x2="19" y1="8" y2="14"></line>
                <line x1="22" x2="16" y1="11" y2="11"></line>
              </svg>
              {t("Demandez un accès à VERDEO")}
            </a>
          </div>
        </Form>
      </CardContent>
    </Card>
  );
};

export default LoginForm;
